"use client"

import { useMemo, useState } from "react"
import { FcMoneyTransfer } from "react-icons/fc"
import { IoArrowBackCircleSharp, IoArrowForwardCircle } from "react-icons/io5"
import "swiper/css"
import "swiper/css/navigation"
import { Autoplay, Navigation } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import type { Category, Subcategory } from "../../../Hooks/useFetchAllCategories"
import useFetchAllCategories from "../../../Hooks/useFetchAllCategories"
import HowItWorksModal from "./HowItWorksModal"
import CategoryCard from "./categories/CategoryCard"
import FeatureCard from "./categories/FeatureCard"
import CategoryBottomSheet from "./categories/CategoryBottomSheet"
import type { FeatureItem } from "../types/categories"

const FEATURED_CATEGORIES = [
    "Électronique",
    "Véhicules",
    "Maison et Meubles",
    "Équipement Commercial et Outils",
    "Services",
]

const FEATURE_ITEMS: FeatureItem[] = [
    {
        icon: <FcMoneyTransfer className="h-6 w-6 text-primary-default" />,
        title: "Comment ça marche ?",
    },
]

const FeaturedCategoriesSlider: React.FC = () => {
    const [sheetOpen, setSheetOpen] = useState(false)
    const [selectedCategory, setSelectedCategory] = useState<Category | null>(null)
    const [search, setSearch] = useState("")
    const [isHowItWorksOpen, setIsHowItWorksOpen] = useState(false)
    const { categories, subcategories, loading, error } = useFetchAllCategories()
    const swiperId = "featured-categories"

    // Filter categories to only show featured ones, ordered by number of ads
    const featuredCategories = useMemo(() => {
        return categories
            .filter((cat) => FEATURED_CATEGORIES.includes(cat.name))
            .sort((a, b) => (b.adCount || 0) - (a.adCount || 0))
    }, [categories])

    // Generate consistent colors for each category
    const getCategoryColor = (categoryName: string) => {
        const colorMap: Record<string, string> = {
            Électronique: "hsl(210, 70%, 90%)",
            Véhicules: "hsl(120, 70%, 90%)",
            "Maison et Meubles": "hsl(30, 70%, 90%)",
            "Équipement Commercial et Outils": "hsl(280, 70%, 90%)",
            Services: "hsl(0, 70%, 90%)",
        }
        return colorMap[categoryName] || "hsl(0, 0%, 90%)"
    }

    // Filter subcategories by selected category and search, ordered by number of ads
    const filteredSubcategories = useMemo(() => {
        if (!selectedCategory) return []
        return subcategories
            .filter((sc) => sc.category_id === selectedCategory.id)
            .filter((sc) => sc.name.toLowerCase().includes(search.toLowerCase()))
            .sort((a, b) => (b.adCount || 0) - (a.adCount || 0))
    }, [selectedCategory, subcategories, search])

    // Handle category tap
    const handleCategoryTap = (cat: Category) => {
        setSelectedCategory(cat)
        setSheetOpen(true)
        setSearch("")
    }

    // Handle subcategory tap
    const handleSubcategoryTap = (subcat: Subcategory) => {
        window.location.href = `/search/${selectedCategory?.id}/${subcat.id}`
        setSheetOpen(false)
    }

    // Prepare slides with "Comment ça marche" first
    const allSlides = useMemo(() => {
        const featureSlides = FEATURE_ITEMS.map((feature) => (
            <SwiperSlide key={`feature-${feature.title}`}>
                <FeatureCard feature={feature} onTap={() => setIsHowItWorksOpen(true)} />
            </SwiperSlide>
        ))

        const categorySlides = featuredCategories.map((cat) => (
            <SwiperSlide key={`category-${cat.id}`}>
                <CategoryCard
                    category={cat}
                    onTap={handleCategoryTap}
                    color={getCategoryColor(cat.name)}
                />
            </SwiperSlide>
        ))

        return [...featureSlides, ...categorySlides]
    }, [featuredCategories])

    if (loading)
        return (
            <div className="w-full p-3">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                    {[...Array(5)].map((_, index) => (
                        <div key={index} className="flex flex-col items-center p-3 rounded-xl bg-surface-tertiary animate-pulse">
                            <div className="w-12 h-12 rounded-full bg-solid-100 mb-2" />
                            <div className="h-3 w-16 bg-solid-100 rounded-full" />
                        </div>
                    ))}
                </div>
            </div>
        )

    if (error)
        return (
            <div className="w-full p-3">
                <div className="flex flex-col items-center justify-center p-6 bg-surface-secondary rounded-xl">
                    <div className="w-16 h-16 rounded-full bg-solid-100 mb-3 animate-pulse" />
                    <div className="h-4 w-32 bg-solid-100 rounded-full mb-2 animate-pulse" />
                    <div className="h-3 w-24 bg-solid-100 rounded-full animate-pulse" />
                </div>
            </div>
        )

    return (
        <div className="w-full">
            {/* <h2 className="text-lg font-semibold mb-4 px-3 text-content-default">Catégories Populaires</h2> */}

            <div className="relative mt-6">
                {/* Custom navigation buttons */}
                <div className={`absolute top-[35%] left-0 z-10 swiper-button-prev-custom-${swiperId}`}>
                    <IoArrowBackCircleSharp size={25} className="cursor-pointer text-primary-default" />
                </div>
                <div className={`absolute right-0 top-[35%] z-10 swiper-button-next-custom-${swiperId}`}>
                    <IoArrowForwardCircle size={25} className="cursor-pointer text-primary-default" />
                </div>

                <div className="mx-10">
                    <Swiper
                        modules={[Autoplay, Navigation]}
                        spaceBetween={8}
                        slidesPerView={3}
                        loop={true}
                        loopAdditionalSlides={10}
                        speed={800}
                        slidesPerGroup={1}
                        navigation={{
                            nextEl: `.swiper-button-next-custom-${swiperId}`,
                            prevEl: `.swiper-button-prev-custom-${swiperId}`,
                        }}
                        autoplay={{
                            delay: 3000,
                            disableOnInteraction: false,
                        }}
                        breakpoints={{
                            640: {
                                slidesPerView: 4,
                                spaceBetween: 12,
                            },
                            1024: {
                                slidesPerView: 5,
                                spaceBetween: 12,
                            },
                        }}
                        className="flex items-center"
                    >
                        {allSlides}
                    </Swiper>
                </div>
            </div>

            {/* BottomSheet for subcategories */}
            <CategoryBottomSheet
                open={sheetOpen}
                onClose={() => setSheetOpen(false)}
                selectedCategory={selectedCategory}
                subcategories={filteredSubcategories}
                search={search}
                onSearchChange={setSearch}
                onSubcategoryTap={handleSubcategoryTap}
            />

            {/* How It Works Modal */}
            <HowItWorksModal isOpen={isHowItWorksOpen} onClose={() => setIsHowItWorksOpen(false)} />
        </div>
    )
}

export default FeaturedCategoriesSlider
